/**
 * LeftSidebar - Classic nav bar with icon + link for Messages, Clubs, Notifications, Events, Store, Profile.
 * Log out button at bottom.
 *
 * Responsive:
 * - hidden under md (mobile uses BottomTabBar + MobileDrawer)
 * - md: icon-only rail (w-20)
 * - lg: full sidebar with labels (w-1/5)
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/auth.reducer';
import { useNotificationCount } from '../../utils/NotificationCountContext';
import { useMessageCount } from '../../utils/MessageCountContext';
import { cleanupDeviceTokenOnLogout } from '../../helpers/deviceTokenHelpers';
import { clearAppIconBadge } from '../../helpers/appBadgeHelpers';
import NavIcon from './NavIcon';

const NAV_ITEMS = [
  { to: '/messages', label: 'Messages', icon: 'messages' },
  { to: '/clubs', label: 'Clubs', icon: 'clubs' },
  { to: '/notifications', label: 'Notifications', icon: 'notifications' },
  { to: '/events', label: 'Events', icon: 'events' },
  { to: '/store', label: 'Store', icon: 'store' },
  { to: null, label: 'Profile', icon: 'profile' },
];

function LeftSidebar() {
  const { unreadCount } = useNotificationCount();
  const { unreadMessageCount } = useMessageCount();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await cleanupDeviceTokenOnLogout();
    await clearAppIconBadge();
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // network failure shouldn't block logout
    }
    dispatch(logout());
    navigate('/');
  };

  return (
    <aside
      className="hidden md:flex md:w-20 lg:w-1/5 flex-shrink-0 min-h-0 overflow-y-auto border-r border-shadow bg-charcoal/50"
      data-testid="left-sidebar"
    >
      <nav className="p-4 flex flex-col gap-1 w-full">
        {NAV_ITEMS.map((item) => {
          const to = item.to ?? '/profile';
          const isNotifications = item.label === 'Notifications';
          const isMessages = item.label === 'Messages';
          const badgeCount = isMessages ? unreadMessageCount : isNotifications ? unreadCount : 0;
          return (
            <Link
              key={item.label}
              to={to}
              className="relative flex items-center justify-center lg:justify-start gap-3 w-full py-3 px-3 rounded-lg text-ash text-sm hover:bg-shadow hover:text-ether transition-colors"
            >
              <span className="relative inline-flex">
                <NavIcon icon={item.icon} className="w-5 h-5" />
                {badgeCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full bg-ether text-void text-[10px] font-bold flex items-center justify-center"
                    aria-label={
                      isMessages
                        ? `${badgeCount} unread message conversations`
                        : `${badgeCount} unread notifications`
                    }
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </span>
              <span className="hidden lg:inline">{item.label}</span>
            </Link>
          );
        })}
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center justify-center lg:justify-start gap-3 w-full py-3 px-3 rounded-lg text-ash text-sm hover:bg-shadow hover:text-energy transition-colors text-left mt-2"
          data-testid="logout-button"
        >
          <NavIcon icon="logout" className="w-5 h-5" />
          <span className="hidden lg:inline">Log out</span>
        </button>
      </nav>
    </aside>
  );
}

export default LeftSidebar;
